import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Clock,
  CheckCircle,
  AlertCircle,
  Settings,
  Plus,
  Calendar,
  Banknote,
  Clock3,
  UserX,
} from "lucide-react";
import { JobSetupWizard } from "./index";
import { HoursChangeModal, EndEmploymentModal, UnifiedPayModal } from "./modals";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface JobTimelineEntry {
  id: number;
  event_type: string;
  effective_date: string;
  payload: any;
  created_at: string;
}

interface JobTimelineSummary {
  employee_id: number;
  has_initial_setup: boolean;
  active_entry: JobTimelineEntry | null;
  current_job_data: {
    salary: number;
    salary_unit: string;
    pay_schedule: string;
    effective_date: string;
  } | null;
  pending_changes_count: number;
  has_pending_changes: boolean;
  next_pending_change: JobTimelineEntry | null;
}

interface JobQuickActionsProps {
  employeeId: number;
  onSetupComplete?: () => void;
  showAdvanced?: boolean;
  onToggleAdvanced?: () => void;
}

export const JobQuickActions: React.FC<JobQuickActionsProps> = ({
  employeeId,
  onSetupComplete,
  showAdvanced = false,
  onToggleAdvanced,
}) => {
  const [summary, setSummary] = useState<JobTimelineSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [showHoursChangeModal, setShowHoursChangeModal] = useState(false);
  const [showUnifiedPayModal, setShowUnifiedPayModal] = useState<null | 'pay'>(null);
  const [showEndEmploymentModal, setShowEndEmploymentModal] = useState(false);

  useEffect(() => {
    fetchTimelineSummary();
  }, [employeeId]);

  const fetchTimelineSummary = async () => {
    try {
      setLoading(true);
      const response = await ApiService.request<JobTimelineSummary>(
        EndpointKeys.GET_JOB_TIMELINE_SUMMARY,
        {
          method: "GET",
          urlParams: { employeeId },
        },
      );
      setSummary(response);
    } catch (error) {
      console.error("Failed to fetch job timeline summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetupWizard(false);
    fetchTimelineSummary(); // Refresh the summary
    onSetupComplete?.();
  };

  const handleActionSuccess = () => {
    fetchTimelineSummary(); // Refresh the summary after any action
  };

  const formatDate = (dateString: string) => {
    // Handle ISO date strings (YYYY-MM-DD) by explicitly creating a date in local timezone
    // This prevents timezone issues where "2025-10-31" might display as "30/10/2025"
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getEventTypeDisplay = (eventType: string) => {
    const eventDisplayMap: Record<string, string> = {
      INITIAL_SETUP_COMMITTED: "Initial Setup",
      JOB_START_CONFIRMED: "Job Started",
      PAY_CHANGED: "Pay Changed",
      HOURS_CHANGED: "Hours Changed",
      BASIS_SWITCHED: "Pay Basis Changed",
      FUTURE_CHANGE_SCHEDULED: "Future Change",
      EMPLOYMENT_ENDED: "Employment Ended",
    };
    return eventDisplayMap[eventType] || eventType;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Job Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5" />
            Job Information
          </CardTitle>
          <CardDescription>
            Manage job setup, pay rates, and working hours
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Setup Section */}
          {!summary?.has_initial_setup && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Initial Setup</h3>
                <Badge
                  variant="outline"
                  className="text-orange-600 border-orange-600"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Setup Required
                </Badge>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Complete the job setup wizard to configure start date, pay,
                  and working hours.
                </AlertDescription>
              </Alert>

              <Button
                onClick={() => setShowSetupWizard(true)}
                className="w-full"
                size="lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Start Job Setup Wizard
              </Button>
            </div>
          )}

          {/* Current Job Status */}
          {summary?.has_initial_setup && (summary.active_entry || summary.current_job_data) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Current Job Status</h3>
                <Badge
                  variant="default"
                  className="text-green-600 bg-green-50 border-green-600"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              </div>
              
              {/* Display current job information */}
              {summary.current_job_data && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Current Pay:</span>
                      <div className="text-lg font-semibold text-gray-900">
                        £{summary.current_job_data.salary.toLocaleString()} {summary.current_job_data.salary_unit.toLowerCase()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Pay Schedule:</span>
                      <div className="text-lg font-semibold text-gray-900">
                        {summary.current_job_data.pay_schedule}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Effective from: {formatDate(summary.current_job_data.effective_date)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pending Changes */}
          {summary?.has_pending_changes && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Pending Changes</h3>
                <Badge
                  variant="outline"
                  className="text-blue-600 border-blue-600"
                >
                  <Clock className="h-3 w-3 mr-1" />
                  {summary.pending_changes_count} Pending
                </Badge>
              </div>

              {summary.next_pending_change && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-900">
                      {getEventTypeDisplay(
                        summary.next_pending_change.event_type,
                      )}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700">
                    Effective: {formatDate(summary.next_pending_change.effective_date)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Quick Actions */}
          {summary?.has_initial_setup && (
            <div className="space-y-4">
              <Separator />

              <h3 className="text-lg font-medium">Quick Actions</h3>

              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" size="sm" onClick={() => setShowUnifiedPayModal('pay')}>
                  <Banknote className="h-4 w-4 mr-2" />
                  Change Pay
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHoursChangeModal(true)}
                >
                  <Clock3 className="h-4 w-4 mr-2" />
                  Update Hours
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEndEmploymentModal(true)}
                  className="text-red-600 hover:text-red-700 hover:border-red-300"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  End Employment
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                All job actions available • Pay changes, hours, and termination
              </p>
            </div>
          )}

          {/* Advanced Settings Toggle */}
          <div className="space-y-4">
            <Separator />

            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleAdvanced}
              className="w-full justify-start"
            >
              <Settings className="h-4 w-4 mr-2" />
              {showAdvanced ? "Hide" : "Show"} Advanced Settings
            </Button>

            {showAdvanced && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <p className="text-sm text-gray-600 mb-3">
                  Advanced job configuration options will be available here.
                </p>
                <Button variant="outline" size="sm" disabled>
                  Advanced Job Timeline
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <JobSetupWizard
          employeeId={employeeId}
          onComplete={handleSetupComplete}
          onCancel={() => setShowSetupWizard(false)}
        />
      )}

      {/* Hours Change Modal */}
      <HoursChangeModal
        isOpen={showHoursChangeModal}
        onClose={() => setShowHoursChangeModal(false)}
        employeeId={employeeId}
        onSuccess={handleActionSuccess}
      />

      {/* Unified Pay Modal */}
      <UnifiedPayModal
        isOpen={!!showUnifiedPayModal}
        onClose={() => setShowUnifiedPayModal(null)}
        employeeId={employeeId}
        onSuccess={handleActionSuccess}
        initialAction={showUnifiedPayModal || undefined}
        currentPayType={
          summary?.current_job_data?.salary_unit === 'HOURLY' ? 'hourly' : 'salary'
        }
      />

      {/* End Employment Modal */}
      <EndEmploymentModal
        isOpen={showEndEmploymentModal}
        onClose={() => setShowEndEmploymentModal(false)}
        employeeId={employeeId}
        onSuccess={handleActionSuccess}
      />
    </>
  );
};
